/**
 * Create Bridge Session wizard: select/add user, company, and domain.
 * Creates session with minimal payload, then navigates to domain-specific flow
 * (Business → business-objective page, MarketFit → marketfit page).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Form, Button, Typography, message, Select, Input } from 'antd';
import {
  createBridgeSession,
  createUser,
  createCompany,
  type CreateBridgeSessionPayload,
  type LocalUser,
  type LocalCompany,
} from '../../services/localBridgeSessionApi';

const { Text } = Typography;

const DOMAIN_OPTIONS = [
  { value: 'Business', label: 'Business' },
  { value: 'Strategy', label: 'Strategy' },
  { value: 'Resource', label: 'Resource' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Operations', label: 'Operations' },
  { value: 'Revenue', label: 'Revenue' },
  { value: 'MarketFit', label: 'MarketFit' },
];

interface CreateBridgeSessionWizardProps {
  open: boolean;
  onClose: () => void;
  userId?: string;
  companyId?: string;
  users: LocalUser[];
  companies: LocalCompany[];
  onUsersCompaniesChange?: () => void;
  onSessionCreated?: () => void;
}

export const CreateBridgeSessionWizard: React.FC<CreateBridgeSessionWizardProps> = ({
  open,
  onClose,
  userId: initialUserId,
  companyId: initialCompanyId,
  users,
  companies,
  onUsersCompaniesChange,
  onSessionCreated,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm<{ domain: string }>();
  const [submitting, setSubmitting] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(initialUserId);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | undefined>(initialCompanyId);
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [addCompanyModalOpen, setAddCompanyModalOpen] = useState(false);
  const [addUserForm] = Form.useForm<{ name: string; email?: string }>();
  const [addCompanyForm] = Form.useForm<{ name: string }>();
  const [addingUser, setAddingUser] = useState(false);
  const [addingCompany, setAddingCompany] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedUserId(initialUserId);
      setSelectedCompanyId(initialCompanyId);
    }
  }, [open, initialUserId, initialCompanyId]);

  const handleAddUser = async () => {
    try {
      const values = await addUserForm.validateFields();
      setAddingUser(true);
      const user = await createUser(values.name, values.email);
      if (user) {
        message.success('User created');
        onUsersCompaniesChange?.();
        setSelectedUserId(user.id);
        addUserForm.resetFields();
        setAddUserModalOpen(false);
      } else {
        message.error('Failed to create user');
      }
    } catch (e) {
      if (e && typeof e === 'object' && 'errorFields' in e) return;
      message.error('Please enter user name');
    } finally {
      setAddingUser(false);
    }
  };

  const handleAddCompany = async () => {
    try {
      const values = await addCompanyForm.validateFields();
      setAddingCompany(true);
      const company = await createCompany(values.name);
      if (company) {
        message.success('Company created');
        onUsersCompaniesChange?.();
        setSelectedCompanyId(company.id);
        addCompanyForm.resetFields();
        setAddCompanyModalOpen(false);
      } else {
        message.error('Failed to create company');
      }
    } catch (e) {
      if (e && typeof e === 'object' && 'errorFields' in e) return;
      message.error('Please enter company name');
    } finally {
      setAddingCompany(false);
    }
  };

  const handleCommit = async () => {
    if (!selectedUserId || !selectedCompanyId) {
      message.warning('Please select a user and a company');
      return;
    }
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload: CreateBridgeSessionPayload = {
        user_id: selectedUserId,
        company_id: selectedCompanyId,
        current_revenue: 0,
        current_expense: 0,
        domain: values.domain?.trim() || undefined,
      };
      const session = await createBridgeSession(payload);
      if (session) {
        message.success('Bridge session created');
        onSessionCreated?.();
        onClose();
        form.resetFields();
        const domain = values.domain?.trim() || session.domain;
        if (domain === 'MarketFit') {
          navigate(`/local-bridge/marketfit?sessionId=${session.id}`);
        } else if (domain === 'Business') {
          navigate(`/local-bridge/business-objective-workshop?sessionId=${session.id}`);
        } else {
          message.info('This domain flow is coming soon.');
          navigate('/local-bridge');
        }
      } else {
        message.error('Failed to create session');
      }
    } catch (e) {
      if (e && typeof e === 'object' && 'errorFields' in e) return;
      message.error('Please fill required fields');
    } finally {
      setSubmitting(false);
    }
  };

  const userOptions = users.map((u) => ({ value: u.id, label: u.name || u.id }));
  const companyOptions = companies.map((c) => ({ value: c.id, label: c.name || c.id }));

  return (
    <>
      <Modal
        title="Create Bridge Session"
        open={open}
        onCancel={onClose}
        footer={null}
        width={560}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ domain: 'Business', estimated_months: 12 }}
        >
          <Form.Item label="User" required>
            <div style={{ display: 'flex', gap: 8 }}>
              <Select
                placeholder="Select user"
                value={selectedUserId}
                onChange={setSelectedUserId}
                options={userOptions}
                style={{ flex: 1 }}
                allowClear
              />
              <Button onClick={() => setAddUserModalOpen(true)}>Add new</Button>
            </div>
          </Form.Item>
          <Form.Item label="Company" required>
            <div style={{ display: 'flex', gap: 8 }}>
              <Select
                placeholder="Select company"
                value={selectedCompanyId}
                onChange={setSelectedCompanyId}
                options={companyOptions}
                style={{ flex: 1 }}
                allowClear
              />
              <Button onClick={() => setAddCompanyModalOpen(true)}>Add new</Button>
            </div>
          </Form.Item>

          <Form.Item name="domain" label="Session domain" rules={[{ required: true }]}>
            <Select options={DOMAIN_OPTIONS} placeholder="Select domain" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Button
              type="primary"
              size="large"
              block
              onClick={handleCommit}
              loading={submitting}
              disabled={!selectedUserId || !selectedCompanyId}
            >
              Create
            </Button>
            <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
              Creates the session and takes you to the domain-specific flow.
            </Text>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Add new user"
        open={addUserModalOpen}
        onCancel={() => setAddUserModalOpen(false)}
        onOk={handleAddUser}
        confirmLoading={addingUser}
        okText="Create"
      >
        <Form form={addUserForm} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="User name" />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input type="email" placeholder="Optional" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Add new company"
        open={addCompanyModalOpen}
        onCancel={() => setAddCompanyModalOpen(false)}
        onOk={handleAddCompany}
        confirmLoading={addingCompany}
        okText="Create"
      >
        <Form form={addCompanyForm} layout="vertical">
          <Form.Item name="name" label="Company name" rules={[{ required: true }]}>
            <Input placeholder="Company name" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CreateBridgeSessionWizard;
